// =====================================================
// TRADER DISCIPLINE INDEX (TDI) SCORE CARD
// Displays the FICO-like discipline score with breakdown
// =====================================================

import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../SafeIcon';
import { FiMessageCircle } from 'react-icons/fi';
import { 
  TDIScore, 
  TDIComponents,
  getTierFromScore, 
  getTierColor, 
  getTierBgColor,
  getTrendIcon,
  getTrendColor,
  getComponentLabel,
  getComponentWeight,
} from '@/types/disciplineScore';
import { calculateTDIScore, getLatestTDIScore } from '@/services/disciplineScoreService';
import TDICoachingModal from './TDICoachingModal';

interface TDIScoreCardProps {
  onScoreCalculated?: (score: TDIScore) => void;
  compact?: boolean;
}

export function TDIScoreCard({ onScoreCalculated, compact = false }: TDIScoreCardProps) {
  const [tdiData, setTdiData] = useState<TDIScore | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCoachingModalOpen, setIsCoachingModalOpen] = useState(false);

  useEffect(() => {
    loadTDIScore();
  }, []);

  const loadTDIScore = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await calculateTDIScore(30);
      
      // If not enough recent data, fallback to last known score
      if (result.success && result.tdi_score === null) {
        const lastKnown = await getLatestTDIScore();
        if (lastKnown && lastKnown.tdi_score !== null) {
          // Show last known score with a note
          lastKnown.message = `Last recorded score (${result.decisions_counted || 0} decisions in last 30 days). Add new entries to update.`;
          setTdiData(lastKnown);
          if (onScoreCalculated) onScoreCalculated(lastKnown);
          return;
        }
      }
      
      setTdiData(result);
      
      if (result.success && onScoreCalculated) {
        onScoreCalculated(result);
      }
    } catch (err) {
      setError('Failed to load TDI score');
      console.error('[TDIScoreCard] Error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className={`bg-card/50 backdrop-blur-sm rounded-xl border border-border/50 ${compact ? 'p-4' : 'p-6'}`}>
        <div className="animate-pulse">
          <div className="h-4 bg-muted rounded w-1/3 mb-4" />
          <div className="h-16 bg-muted rounded w-1/2 mb-4" />
          <div className="h-3 bg-muted rounded w-full" />
        </div>
      </div>
    );
  }

  if (error || !tdiData?.success) {
    return (
      <div className={`bg-card/50 backdrop-blur-sm rounded-xl border border-border/50 ${compact ? 'p-4' : 'p-6'}`}>
        <p className="text-sm text-muted-foreground">
          {error || tdiData?.error || 'Unable to calculate TDI'}
        </p>
      </div>
    );
  }

  // Not enough data and no historical score
  if (tdiData.tdi_score === null) {
    return (
      <div className={`bg-card/50 backdrop-blur-sm rounded-xl border border-border/50 ${compact ? 'p-4' : 'p-6'}`}>
        <div className="text-center">
          <h3 className="text-lg font-semibold mb-2">Trader Discipline Index</h3>
          <p className="text-sm text-muted-foreground mb-4">
            {tdiData.message || 'Building your score...'}
          </p>
          <div className="flex items-center justify-center gap-2">
            <div className="w-full bg-muted rounded-full h-2">
              <motion.div 
                className="bg-primary h-2 rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${((tdiData.decisions_counted || 0) / 5) * 100}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {tdiData.decisions_counted || 0}/5 decisions
            </span>
          </div>
        </div>
      </div>
    );
  }

  const tier = tdiData.tier || getTierFromScore(tdiData.tdi_score);
  const tierColor = getTierColor(tier);
  const tierBgColor = getTierBgColor(tier);
  const trendIcon = tdiData.trend ? getTrendIcon(tdiData.trend) : '';
  const trendColor = tdiData.trend ? getTrendColor(tdiData.trend) : '';

  if (compact) {
    return (
      <div className={`rounded-xl border p-4 ${tierBgColor}`}>
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-muted-foreground uppercase tracking-wider">TDI Score</p>
            <div className="flex items-baseline gap-2">
              <span className={`text-3xl font-bold ${tierColor}`}>
                {tdiData.tdi_score}
              </span>
              {tdiData.trend && tdiData.trend !== 'NEW' && (
                <span className={`text-sm ${trendColor}`}>
                  {trendIcon} {Math.abs(tdiData.trend_change || 0)}
                </span>
              )}
            </div>
          </div>
          <div className="text-right">
            <span className={`text-sm font-medium ${tierColor}`}>{tier}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div 
      className="rounded-2xl bg-gray-900/80 border border-white/10 p-6 backdrop-blur-sm"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h3 className="text-lg font-semibold text-white">Trader Discipline Index</h3>
          <p className="text-xs text-gray-500">
            FICO-like score for trading discipline (300-850)
          </p>
        </div>
        <div className={`px-3 py-1.5 rounded-full text-xs font-semibold ${tierBgColor} ${tierColor}`}>
          {tier}
        </div>
      </div>

      {/* Main Score */}
      <div className="flex items-center gap-8 mb-6">
        <div className="relative">
          {/* Score Circle */}
          <svg className="w-28 h-28 transform -rotate-90">
            <circle
              cx="56"
              cy="56"
              r="48"
              fill="none"
              stroke="currentColor"
              strokeWidth="6"
              className="text-gray-800"
            />
            <motion.circle
              cx="56"
              cy="56"
              r="48"
              fill="none"
              stroke="currentColor"
              strokeWidth="6"
              strokeLinecap="round"
              className={tierColor}
              initial={{ strokeDasharray: '0, 302' }}
              animate={{ 
                strokeDasharray: `${((tdiData.tdi_score - 300) / 550) * 302}, 302` 
              }}
              transition={{ duration: 1, ease: 'easeOut' }}
            />
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-4xl font-bold ${tierColor}`}>
              {tdiData.tdi_score}
            </span>
            {tdiData.trend && tdiData.trend !== 'NEW' && (
              <span className={`text-xs ${trendColor}`}>
                {trendIcon} {tdiData.trend_change > 0 ? '+' : ''}{tdiData.trend_change}
              </span>
            )}
          </div>
        </div>

        {/* Score Range */}
        <div className="flex-1">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>300</span>
            <span>850</span>
          </div>
          <div className="relative h-3 bg-gray-800 rounded-full overflow-hidden">
            {/* Tier markers - Purple/Cyan gradient theme */}
            <div className="absolute inset-0 flex">
              <div className="w-[45%] bg-gray-700" /> {/* 300-550: DEVELOPING */}
              <div className="w-[18%] bg-purple-500/40" /> {/* 550-650: FAIR */}
              <div className="w-[18%] bg-cyan-500/40" /> {/* 650-750: GOOD */}
              <div className="w-[19%] bg-emerald-500/40" /> {/* 750-850: EXCELLENT */}
            </div>
            {/* Current position */}
            <motion.div
              className={`absolute top-0 bottom-0 w-1.5 rounded-full ${tierColor.replace('text-', 'bg-')}`}
              initial={{ left: '0%' }}
              animate={{ left: `${((tdiData.tdi_score - 300) / 550) * 100}%` }}
              transition={{ duration: 1, ease: 'easeOut' }}
            />
          </div>
          <div className="flex justify-between text-[10px] text-gray-500 mt-1">
            <span>DEVELOPING</span>
            <span>FAIR</span>
            <span>GOOD</span>
            <span>EXCELLENT</span>
          </div>
        </div>
      </div>

      {/* TDI v2 - BTDI & CETDI Breakdown */}
      {(tdiData.btdi_score !== undefined || tdiData.cetdi_score !== undefined) && (
        <div className="space-y-4 pt-4 border-t border-white/5">
          <h4 className="text-sm font-medium text-gray-400">TDI v2 Breakdown</h4>
          
          {/* BTDI & CETDI scores */}
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-purple-500/10 rounded-lg p-3 border border-purple-500/20">
              <p className="text-xs text-purple-400 uppercase tracking-wider">Behavior TDI</p>
              <p className="text-2xl font-bold text-purple-300">
                {tdiData.btdi_score?.toFixed(0) ?? '—'}
              </p>
              <p className="text-xs text-gray-500">60% weight</p>
            </div>
            <div className="bg-cyan-500/10 rounded-lg p-3 border border-cyan-500/20">
              <p className="text-xs text-cyan-400 uppercase tracking-wider">Capital Efficiency TDI</p>
              <p className="text-2xl font-bold text-cyan-300">
                {tdiData.cetdi_score?.toFixed(0) ?? '—'}
              </p>
              <p className="text-xs text-gray-500">40% weight</p>
            </div>
          </div>

          {/* CETDI Capital Preserved breakdown */}
          {tdiData.cetdi_components && (
            <div className="bg-gray-800/50 rounded-lg p-3 border border-white/5">
              <p className="text-xs text-gray-400 uppercase tracking-wider mb-2">Counterfactual Proof</p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-emerald-400">Good Skips (Losses Avoided)</p>
                  <p className="text-lg font-semibold text-emerald-300">
                    ${tdiData.cetdi_components.good_skips_usd?.toLocaleString() ?? '0'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-red-400">Bad Skips (Opportunities Missed)</p>
                  <p className="text-lg font-semibold text-red-300">
                    ${tdiData.cetdi_components.bad_skips_usd?.toLocaleString() ?? '0'}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Directional Bias */}
          {tdiData.directional_bias_adjustment !== undefined && tdiData.directional_bias_adjustment !== 0 && (
            <div className="text-xs text-yellow-400 flex items-center gap-1">
              <span>⚠️</span>
              <span>Directional bias adjustment: {tdiData.directional_bias_adjustment > 0 ? '+' : ''}{tdiData.directional_bias_adjustment}</span>
            </div>
          )}
        </div>
      )}

      {/* Legacy Component Breakdown (v1 fallback) */}
      {tdiData.components && !tdiData.btdi_score && (
        <div className="space-y-3 pt-4 border-t border-white/5">
          <h4 className="text-sm font-medium text-gray-400">Score Components</h4>
          {(Object.keys(tdiData.components) as Array<keyof TDIComponents>).map((key) => {
            const score = tdiData.components![key];
            const weight = getComponentWeight(key);
            const label = getComponentLabel(key);
            
            return (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm text-gray-400">{label}</span>
                <span className="text-sm font-medium text-white">
                  {score.toFixed(0)}/100
                  <span className="text-gray-500 ml-1">({weight}%)</span>
                </span>
              </div>
            );
          })}
        </div>
      )}

      {/* Footer */}
      <div className="mt-4 pt-4 border-t border-white/5 flex flex-col gap-2">
        {tdiData.message && (
          <p className="text-xs text-amber-400/80 italic">{tdiData.message}</p>
        )}
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>Based on {tdiData.decisions_counted} decisions</span>
          <span>Last {tdiData.period_days || 30} days</span>
        </div>
      </div>

      {/* TDI Coaching Modal */}
      <TDICoachingModal
        isOpen={isCoachingModalOpen}
        onClose={() => setIsCoachingModalOpen(false)}
        tdiData={tdiData ? {
          tdi_score: tdiData.tdi_score ?? undefined,
          tier: tdiData.tier,
          trend: tdiData.trend,
          trend_change: tdiData.trend_change,
          btdi: tdiData.btdi_score,
          cetdi: tdiData.cetdi_score,
          components: {
            behavior: tdiData.components,
            capital: tdiData.cetdi_components
          },
          directional_bias: tdiData.directional_bias_adjustment !== 0 ? {
            has_warning: true,
            dominant_side: tdiData.directional_bias_adjustment < 0 ? 'LONG' : 'SHORT'
          } : undefined,
          decisions_counted: tdiData.decisions_counted,
          period_days: tdiData.period_days
        } : null}
      />
    </motion.div>
  );
}

export default TDIScoreCard;
